import { useCallback, useEffect, useRef } from 'react'
import * as cache from 'state/cache'
import CableApp from 'actioncable'
import { withChannelAuth } from 'state/channels/channelAuth'
import useStore from 'state/knovStore'
import { hydrateViewerNeutralAnswersIntoQuest } from 'api/api'
import { cacheQuestPreservingPendingOptimisticAnswers } from 'state/pendingOptimisticAnswers'
import { reconcileQuestOnSubscribe } from './reconcileOnSubscribe'
import queryClient from 'api/queryClient'

const REPORT = false

// TODO: have this map be stored elsewhere and imported so that all
// components can use it
const msgTypes = {
    UPDATE_QUEST: 'update quest',
    // A change signal without the quest (QuestChannel.broadcast_changed), for
    // threads too large to send whole; consumers reread what they show.
    QUEST_CHANGED: 'quest changed',
    SET_CHATGPT_PENDING: 'set chatgpt pending',
    SET_CHATGPT_COMPLETE: 'set chatgpt complete',
    SET_CHATGPT_ERROR: 'set chatgpt error',
}

function collectQuestAnswerIdsForJobCleanup(questId: string): string[] {
    const quest = cache.getCachedQuest(questId)
    if (!quest) return []

    const answerIds = new Set<string>()
    if (quest.parent_id) answerIds.add(String(quest.parent_id))

    if (Array.isArray(quest.sorted_answer_ids)) {
        quest.sorted_answer_ids.forEach(answerId => {
            if (answerId) answerIds.add(String(answerId))
        })
    }

    if (Array.isArray(quest.sorted_answers)) {
        quest.sorted_answers.forEach(answer => {
            if (answer?.id) answerIds.add(String(answer.id))
        })
    }

    return Array.from(answerIds)
}

type UseQuestChannelOpts = {
    /**
     * Automatically subscribe on mount and unsubscribe on unmount.
     * Useful for simple consumers that always want a live channel.
     * Default: true
     */
    autoSubscribe?: boolean
    reconcileOnSubscribe?: boolean
    deferReconcileOnSubscribe?: boolean
    deferQuestUpdates?: boolean
    onDeferredQuestUpdate?: (questId: string) => void
    /** Called on every (re)subscription, so a consumer can catch up on what it missed. */
    onConnected?: () => void
}

export default function useQuestChannel(questId: string, opts: UseQuestChannelOpts = {}) {
    const {
        autoSubscribe = true,
        reconcileOnSubscribe = false,
        deferReconcileOnSubscribe = false,
        deferQuestUpdates = false,
        onDeferredQuestUpdate,
        onConnected,
    } = opts
    const set = useStore.getState().set

    const channel = useRef<any>(null)
    const reconcileHandle = useRef<number | null>(null)
    const reconcileUsesIdleCallback = useRef(false)

    const cancelScheduledReconcile = useCallback(() => {
        if (reconcileHandle.current == null) return

        const idleWindow = window as typeof window & {
            cancelIdleCallback?: (handle: number) => void
        }
        if (reconcileUsesIdleCallback.current && idleWindow.cancelIdleCallback) {
            idleWindow.cancelIdleCallback(reconcileHandle.current)
        } else {
            window.clearTimeout(reconcileHandle.current)
        }
        reconcileHandle.current = null
    }, [])

    const reconcile = useCallback(() => {
        if (!reconcileOnSubscribe || !questId) return
        cancelScheduledReconcile()

        if (!deferReconcileOnSubscribe) {
            void reconcileQuestOnSubscribe(questId)
            return
        }

        const run = () => {
            reconcileHandle.current = null
            if (channel.current) void reconcileQuestOnSubscribe(questId)
        }
        const idleWindow = window as typeof window & {
            requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number
        }
        if (idleWindow.requestIdleCallback) {
            reconcileUsesIdleCallback.current = true
            reconcileHandle.current = idleWindow.requestIdleCallback(run, { timeout: 1000 })
        } else {
            reconcileUsesIdleCallback.current = false
            reconcileHandle.current = window.setTimeout(run, 200)
        }
    }, [cancelScheduledReconcile, deferReconcileOnSubscribe, questId, reconcileOnSubscribe])

    const subscribe = useCallback(() => {
        if (!questId || channel.current) return

        // Credentials ride along so a private thread's stream admits its viewers
        // (QuestChannel#subscribed); publicly viewable threads never check them.
        channel.current = CableApp.cable.subscriptions.create(
            withChannelAuth({
                channel: 'QuestChannel',
                stream_from: `quest_${questId}`,
                quest_id: questId,
            }),
            {
                received: async data => {
                    if (data.message === msgTypes.QUEST_CHANGED) {
                        const changedQuestId = String(data.questId || questId)
                        if (onDeferredQuestUpdate) {
                            onDeferredQuestUpdate(changedQuestId)
                        } else {
                            // A plain thread view has no window of its own:
                            // refetch the thread where one is on screen.
                            void queryClient.invalidateQueries({
                                queryKey: ['quest', changedQuestId],
                            })
                        }
                    }

                    if (data.message === msgTypes.UPDATE_QUEST) {
                        let updatedQuest = data.quest
                        if (deferQuestUpdates) {
                            onDeferredQuestUpdate?.(String(updatedQuest?.id || questId))
                        } else if (cache.getCachedQuest(updatedQuest.id)) {
                            // Only cache if we have seen the quest before.
                            // TODO: might be better for the backend to send answers in the update too
                            updatedQuest = await hydrateViewerNeutralAnswersIntoQuest(updatedQuest)
                            cacheQuestPreservingPendingOptimisticAnswers(updatedQuest, {
                                source: 'QuestChannel.update',
                                preserveViewContextFields: true,
                            })
                            // Dispatch a lightweight DOM event to notify components to re-evaluate cached quest data
                            try {
                                window.dispatchEvent(
                                    new CustomEvent('quest_updated', {
                                        detail: { questId: updatedQuest.id },
                                    }),
                                )
                            } catch (e) {}
                        }
                    }

                    if (
                        data.message === msgTypes.SET_CHATGPT_PENDING ||
                        data.message === msgTypes.SET_CHATGPT_ERROR
                    ) {
                        const { questId } = data
                        const shouldTrackQuestPending =
                            !!useStore.getState().questAgentModels?.[String(questId)]
                        const status =
                            data.message === msgTypes.SET_CHATGPT_PENDING ? 'pending' : 'error'

                        set(state => {
                            if (shouldTrackQuestPending) {
                                state.agentReplyPendingStatus[questId] = status
                            } else {
                                delete state.agentReplyPendingStatus[questId]
                            }

                            // Ensure answer-level job spinners cannot remain stuck
                            // when a quest transitions to error without a terminal
                            // answer job_status event.
                            if (status === 'error') {
                                collectQuestAnswerIdsForJobCleanup(questId).forEach(answerId => {
                                    const existingJobStatus = state.answerJobs[answerId]
                                    if (!existingJobStatus) return

                                    if (existingJobStatus.status === 'started') {
                                        const existingMessages = Array.isArray(
                                            existingJobStatus.messages,
                                        )
                                            ? existingJobStatus.messages
                                            : []
                                        const terminalMessage = '❌ Answer failed'

                                        state.answerJobs[answerId] = {
                                            ...existingJobStatus,
                                            status: 'failed',
                                            messages:
                                                existingMessages[existingMessages.length - 1] ===
                                                terminalMessage
                                                    ? existingMessages
                                                    : [...existingMessages, terminalMessage],
                                        }
                                    }
                                })
                            }
                        })
                    }

                    if (data.message === msgTypes.SET_CHATGPT_COMPLETE) {
                        const { questId } = data
                        // deleting the quest id key from the object
                        set(state => {
                            delete state.agentReplyPendingStatus[questId]

                            // Shared fallback for both normal LLM and OpenClaw:
                            // if a terminal answer job_status was missed, convert
                            // started -> finished so the spinner stops without
                            // dropping tool-status messages from the UI.
                            collectQuestAnswerIdsForJobCleanup(questId).forEach(answerId => {
                                const existingJobStatus = state.answerJobs[answerId]
                                if (!existingJobStatus) return

                                if (existingJobStatus.status === 'started') {
                                    const existingMessages = Array.isArray(
                                        existingJobStatus.messages,
                                    )
                                        ? existingJobStatus.messages
                                        : []
                                    const terminalMessage = '✅ Answer complete'

                                    state.answerJobs[answerId] = {
                                        ...existingJobStatus,
                                        status: 'finished',
                                        messages:
                                            existingMessages[existingMessages.length - 1] ===
                                            terminalMessage
                                                ? existingMessages
                                                : [...existingMessages, terminalMessage],
                                    }
                                }
                            })
                        })
                    }
                },

                connected() {
                    if (REPORT && gon.env === 'development')
                        console.log(`Connected to QuestChannel id ${questId}.`)
                    onConnected?.()
                },

                disconnected() {
                    if (REPORT && gon.env === 'development')
                        console.log(`Disconnected from QuestChannel id ${questId}.`)
                },
            },
        )
        if (window.log && gon.env === 'development')
            console.log(
                `subscribed to quest channel id ${questId}`,
                channel.current,
                channel.current?.consumer?.subscriptions?.subscriptions?.length,
            )
        reconcile()
    }, [deferQuestUpdates, onConnected, onDeferredQuestUpdate, questId, reconcile, set])

    const unsubscribe = useCallback(() => {
        cancelScheduledReconcile()
        if (channel.current) {
            channel.current.unsubscribe()
            if (window.log && gon.env === 'development')
                console.log(
                    `unsubscribed from quest channel id ${questId}`,
                    channel.current,
                    channel.current?.consumer?.subscriptions?.subscriptions?.length,
                )
            channel.current = null
        }
    }, [cancelScheduledReconcile, questId])

    useEffect(() => {
        if (!autoSubscribe) return
        if (questId) subscribe()

        return () => unsubscribe()
    }, [questId, autoSubscribe, subscribe, unsubscribe])

    return { subscribe, unsubscribe }
}
